import { useSelector } from 'react-redux';
import debounce from 'lodash/debounce';
import { flow, pipe } from 'fp-ts/function';
import isEmpty from 'lodash/isEmpty';
import { doesExist } from 'utils/tsHelpers';
import { match } from 'ts-pattern';

import { PROJECT_PERMISSIONS } from 'utils/permissions';
import { selectDoesUserHaveRbacPermission } from 'core/userContext/selectors';
import {
  EditPredictiveGoalVariables,
  GoalStatus,
  GoalStatusPredicate,
  PredictiveAudienceGoal,
  PredictiveGoalQuery,
  VocabularyResponse,
} from './types';

import { transformForCreation, validateQuery } from './utils';
import { MAX_GOAL_TOOLTIP_CONTENT, RBAC_USER_LACKS_MANAGE_PERMISSION } from './constants';

export const shouldDisableDeleteByStatus: GoalStatusPredicate = (goal) => {
  const disabledStates = [GoalStatus.PREPARING, GoalStatus.REFRESHING, GoalStatus.DELETING];
  return disabledStates.includes(goal.status);
};

export const shouldDisableEditByStatus: GoalStatusPredicate = (goal) => {
  const disabledStates = [GoalStatus.PREPARING, GoalStatus.REFRESHING, GoalStatus.DELETING];
  return disabledStates.includes(goal.status);
};

export const shouldDisableGoalAction = (
  canManageGoal: boolean,
  goalPredicate: GoalStatusPredicate,
): GoalStatusPredicate => {
  return (goal): boolean => {
    const result = !canManageGoal || goalPredicate(goal);
    return result;
  };
};

export const createGoalUrlByErrorStatus = (goal: PredictiveAudienceGoal): string => {
  let goalUrl = `/predictive-audiences/goals/${goal.id}`;
  if ([GoalStatus.SYSTEM_ERROR, GoalStatus.NEEDS_REVIEW].includes(goal.status)) {
    goalUrl = `/predictive-audiences/goals/${goal.id}/edit`;
  }
  return goalUrl;
};

export const useRbacSelectorVerifier = (permission: number): boolean => {
  const verifyUserHasViewPermission = useSelector(selectDoesUserHaveRbacPermission);
  return verifyUserHasViewPermission(permission);
};

export const useGoalViewPermissionVerifier = () => {
  return useRbacSelectorVerifier(PROJECT_PERMISSIONS.ViewGoals);
};

export const useGoalManagePermissionVerifier = () => {
  return useRbacSelectorVerifier(PROJECT_PERMISSIONS.ManageGoals);
};

export const setGoalPreventionMessage = (
  showManageGoals: boolean,
  showMaxGoals: boolean,
): string => {
  if (showManageGoals) {
    return RBAC_USER_LACKS_MANAGE_PERMISSION;
  }
  if (showMaxGoals) {
    return MAX_GOAL_TOOLTIP_CONTENT;
  }
  return '';
};

export const isVocabularyEmpty = (maybeVocab: VocabularyResponse | undefined): boolean => {
  if (!maybeVocab || maybeVocab.predictiveAudiencesVocabulary === null) {
    return true;
  }
  return false;
};

export const hasErrorStatus = (goal: PredictiveAudienceGoal): boolean => {
  return [GoalStatus.SYSTEM_ERROR, GoalStatus.NEEDS_REVIEW].includes(goal.status);
};

export const parseGoalsFromResponse = (predictiveGoals: {
  predictiveAudienceGoals: any;
}): Array<PredictiveAudienceGoal> => predictiveGoals?.predictiveAudienceGoals || [];

export const isAtGoalLimit = (goalLimit: number, goals: Array<PredictiveAudienceGoal>): boolean => {
  if (!Array.isArray(goals) || goals.length < goalLimit) {
    return false;
  }
  return true;
};

export const createValidateCriteriaDebounce = (
  handleValidationResult: (goalErrors: Set<string>) => void,
) => {
  const flowCriteriaToResult = flow(transformForCreation, validateQuery, handleValidationResult);
  const debouncedFlow = debounce(flowCriteriaToResult, 300);
  return (goalCriteria: PredictiveGoalQuery) => pipe(goalCriteria, debouncedFlow);
};

export const createErrorResultSetter =
  (setErrorResult: (status: boolean) => void) => (goalErrors: Set<string>) =>
    setErrorResult(goalErrors.size > 0);

export const deriveDefaultDuration = (
  disableDuration: boolean,
  vocab: VocabularyResponse,
  defaultDuration: string,
) => {
  let maybeDefaultDuration = disableDuration ? defaultDuration : '';
  if (!disableDuration && !isEmpty(vocab?.predictiveAudiencesVocabulary?.durations)) {
    maybeDefaultDuration = (vocab.predictiveAudiencesVocabulary as any).durations[0]
      .defaultDuration;
  }
  return maybeDefaultDuration;
};

const parseApproxFormatFromParts = (parts: Intl.NumberFormatPart[]) => {
  return match(parts)
    .with([{ type: 'integer' }], ([hundredsValue]) => hundredsValue.value)
    .with([{ type: 'integer' }, { type: 'compact' }], ([integer, compactSign]) => {
      return `${integer.value}${compactSign.value.toLowerCase()}`;
    })
    .with(
      [{ type: 'integer' }, { type: 'decimal' }, { type: 'fraction' }, { type: 'compact' }],
      ([integer, decimal, fraction, compactSign]: Intl.NumberFormatPart[]) => {
        const leadingFraction = fraction.value.slice(0, 1);
        const fractionResult = leadingFraction === '0' ? '' : `${decimal.value}${leadingFraction}`;
        return `${integer.value}${fractionResult}${compactSign.value.toLowerCase()}`;
      },
    )
    .otherwise(() => '');
};

export const formatPreviewUsersCount = (count: number) => {
  if (Number.isNaN(count) || !doesExist(count)) {
    return '';
  }

  const formatter = Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumSignificantDigits: 21,
  });

  return parseApproxFormatFromParts(formatter.formatToParts(count));
};

export const toGoalVariables = (
  predictiveGoal: PredictiveAudienceGoal | undefined,
  goalName: string = '',
): EditPredictiveGoalVariables => {
  const { id, name, description, goal, userProfileField } = predictiveGoal || {
    id: 0,
    name: '',
    description: '',
    goal: {} as PredictiveGoalQuery,
    userProfileField: '',
  };
  return {
    id,
    name: goalName || name,
    description,
    goal,
    userProfileField,
    listIds: [],
  };
};
